import { useEffect, useState } from "react";
import { requestJSON } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementPanel,
  ManagementPagination,
} from "../managementUI";
import { Empty, Note, Table } from "./ui";
type Counts = {
  request_records: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
};
type Team = Counts & {
  team_id: string;
  models: (Counts & { model: string })[];
};
export function LLMUsage() {
  const { t } = useI18n();
  const [hours, setHours] = useState(24);
  const [team, setTeam] = useState("");
  const [draft, setDraft] = useState("");
  const [revision, setRevision] = useState(0);
  const [items, setItems] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Team | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    const end = Date.now();
    const query = new URLSearchParams({
      start_ms: String(end - hours * 3600000),
      end_ms: String(end),
    });
    if (team) query.set("team_id", team);
    requestJSON<{ items: Team[] }>("/api/v1/monitoring/llm-usage?" + query, {
      signal: abort.signal,
    })
      .then((value) => {
        if (!abort.signal.aborted) setItems(value.items);
      })
      .catch((e) => {
        if (!abort.signal.aborted) {
          setItems([]);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [hours, team, revision]);
  return (
    <ManagementPanel
      title={t("Team Token 用量", "Team token usage")}
      actions={
        <button
          className="secondary-button"
          disabled={loading}
          onClick={() => {
            setRevision((v) => v + 1);
            setOffset(0);
          }}
        >
          {t("刷新", "Refresh")}
        </button>
      }
    >
      <div className="monitor-toolbar">
        <label>
          {t("用量时间范围", "Usage window")}
          <select
            value={hours}
            onChange={(e) => {
              setHours(Number(e.target.value));
              setOffset(0);
            }}
          >
            <option value={1}>{t("1小时", "1 hour")}</option>
            <option value={24}>{t("24小时", "24 hours")}</option>
            <option value={168}>{t("7天", "7 days")}</option>
          </select>
        </label>
        <form
          className="monitor-team-filter"
          onSubmit={(e) => {
            e.preventDefault();
            setTeam(draft.trim());
            setOffset(0);
          }}
        >
          <label>
            Team ID
            <input
              maxLength={8}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button className="secondary-button">{t("筛选", "Filter")}</button>
        </form>
      </div>
      <Note>
        {t(
          "按请求结束时间汇总当前 DC 的 LiteLLM 返回记录，包含输入与输出 Token；不是账单或已对账计量。无记录不代表团队不存在，来源失败不显示为零。",
          "Aggregates records returned by this DC's LiteLLM by request end time. Input/output tokens are observations, not an invoice or reconciled ledger. No records do not imply a missing Team; source failures are not zero usage.",
        )}
      </Note>
      {error ? (
        <Empty>{error}</Empty>
      ) : loading ? (
        <Empty>{t("正在读取用量…", "Loading usage…")}</Empty>
      ) : !items.length ? (
        <Empty>
          {t(
            "此范围未返回用量记录。",
            "No usage records returned for this window.",
          )}
        </Empty>
      ) : (
        <>
          <Table
            headers={[
              "Team",
              t("请求记录", "Request records"),
              t("输入 Token", "Input tokens"),
              t("输出 Token", "Output tokens"),
              t("合计 Token", "Total tokens"),
              t("操作", "Actions"),
            ]}
          >
            {items.slice(offset, offset + 20).map((row) => (
              <tr key={row.team_id}>
                <td>
                  {row.team_id}
                  {!/^[0-9abcdefghjkmnpqrstvwxyz]{8}$/.test(row.team_id) ? (
                    <span>
                      {t(
                        "历史来源标识，未映射到当前 Team ID",
                        "Historical source ID; not mapped to a current Team ID",
                      )}
                    </span>
                  ) : null}
                </td>
                <td>{row.request_records.toLocaleString()}</td>
                <td>{row.input_tokens.toLocaleString()}</td>
                <td>{row.output_tokens.toLocaleString()}</td>
                <td>{row.total_tokens.toLocaleString()}</td>
                <td>
                  <button
                    className="secondary-button"
                    onClick={() => setSelected(row)}
                  >
                    {t("模型明细", "By model")}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
          <ManagementPagination
            total={items.length}
            offset={offset}
            limit={20}
            onChange={setOffset}
          />
        </>
      )}
      {selected ? (
        <ManagementDialog
          title={`${selected.team_id} · ${t("模型用量", "Model usage")}`}
          onClose={() => setSelected(null)}
        >
          <Table
            headers={[
              t("模型", "Model"),
              t("请求记录", "Records"),
              t("输入", "Input"),
              t("输出", "Output"),
              t("合计", "Total"),
            ]}
          >
            {selected.models.map((m) => (
              <tr key={m.model}>
                <td>{m.model}</td>
                <td>{m.request_records}</td>
                <td>{m.input_tokens}</td>
                <td>{m.output_tokens}</td>
                <td>{m.total_tokens}</td>
              </tr>
            ))}
          </Table>
          <button
            className="secondary-button"
            onClick={() => setSelected(null)}
          >
            {t("关闭", "Close")}
          </button>
        </ManagementDialog>
      ) : null}
    </ManagementPanel>
  );
}
